// AmbientBackdrop — a single, page-wide pastel atmosphere that lives
// behind the homepage. Four soft Murano blobs (pink · butter · cielo ·
// opal) drift slowly with scroll so sections no longer feel like stacked
// boxes — they share one continuous sky.
//
// Sits position:fixed behind everything; pointer-events: none.

import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

export function AmbientBackdrop() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();

  // Each blob drifts a different distance — gives a dreamy parallax
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-18%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const y3 = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-30%"]);
  const y4 = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      {/* base wash so we never get a hard white seam */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, oklch(0.985 0.004 90) 0%, oklch(0.97 0.012 350) 35%, oklch(0.975 0.012 95) 65%, oklch(0.97 0.012 230) 100%)",
        }}
      />

      {/* Pink — top left */}
      <motion.div
        style={{ y: y1 }}
        className="absolute -left-[12%] top-[6%] h-[55vh] w-[55vh] rounded-full"
      >
        <div
          className="h-full w-full rounded-full opacity-60"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, oklch(0.86 0.10 350 / 0.75), transparent 65%)",
            filter: "blur(40px)",
          }}
        />
      </motion.div>

      {/* Butter yellow — upper right */}
      <motion.div
        style={{ y: y2 }}
        className="absolute -right-[10%] top-[28%] h-[60vh] w-[60vh] rounded-full"
      >
        <div
          className="h-full w-full rounded-full opacity-55"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, oklch(0.93 0.09 95 / 0.65), transparent 65%)",
            filter: "blur(50px)",
          }}
        />
      </motion.div>

      {/* Sky blue — middle left */}
      <motion.div
        style={{ y: y3 }}
        className="absolute -left-[8%] top-[58%] h-[65vh] w-[65vh] rounded-full"
      >
        <div
          className="h-full w-full rounded-full opacity-55"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, oklch(0.86 0.07 230 / 0.7), transparent 65%)",
            filter: "blur(45px)",
          }}
        />
      </motion.div>

      {/* Opal halo — lower right */}
      <motion.div
        style={{ y: y4 }}
        className="absolute -right-[14%] top-[78%] h-[50vh] w-[50vh] rounded-full"
      >
        <div
          className="h-full w-full rounded-full opacity-70"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, oklch(0.97 0.006 90 / 0.85), transparent 70%)",
            filter: "blur(40px)",
          }}
        />
      </motion.div>

      {/* Subtle film grain so the gradients don't band */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
    </div>
  );
}
